import { useState } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import logoImg from "./logo.png";

export default function LoginPage({ onBack }) {
  const { signIn, signUp } = useAuth();

  const [mode, setMode]           = useState("login");
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [loading, setLoading]     = useState(false);
  const [errorMsg, setErrorMsg]   = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const reset = (nextMode) => {
    setMode(nextMode);
    setEmail(""); setPassword(""); setConfirm("");
    setErrorMsg(""); setSuccessMsg("");
  };

  const processEmailInvite = async (userId) => {
    const token = localStorage.getItem("pendingInviteToken");
    if (!token) return;
    localStorage.removeItem("pendingInviteToken");
    let result;
    try {
      const { data: invite } = await supabase
        .from("invite_codes").select("code")
        .eq("link_token", token).eq("is_active", true).maybeSingle();
      if (!invite?.code) {
        result = "초대 링크가 만료됐거나 유효하지 않아요.";
      } else {
        const { data } = await supabase.rpc("use_invite_code", { p_code: invite.code, p_user_id: userId });
        result = data?.success
          ? `🎉 ${data.team_name}팀에 가입됐어요!`
          : (data?.message || "초대 링크가 만료됐거나 유효하지 않아요.");
      }
    } catch {
      result = "초대 처리 중 오류가 발생했어요.";
    }
    localStorage.setItem("inviteJoinResult", result);
  };

  const handleLogin = async () => {
    if (!email.trim() || !password) { setErrorMsg("이메일과 비밀번호를 입력해주세요."); return; }
    setLoading(true); setErrorMsg("");
    const { error, user: loggedUser } = await signIn(email.trim(), password);
    setLoading(false);
    if (error) { setErrorMsg(friendlyError(error.message)); return; }
    if (loggedUser?.id) processEmailInvite(loggedUser.id);
  };

  const handleSignUp = async () => {
    if (!email.trim() || !password || !confirm) { setErrorMsg("모든 항목을 입력해주세요."); return; }
    if (password.length < 6)   { setErrorMsg("비밀번호는 6자 이상이어야 해요."); return; }
    if (password !== confirm)   { setErrorMsg("비밀번호가 일치하지 않아요."); return; }
    setLoading(true); setErrorMsg("");
    try {
      const { error } = await signUp(email.trim(), password);
      setLoading(false);
      if (error) {
        setErrorMsg(friendlyError(error.message));
        return;
      }
      setSuccessMsg("가입이 완료됐어요! 이메일을 확인하거나 바로 로그인해주세요.");
      setMode("login");
      setPassword(""); setConfirm("");
    } catch (e) {
      setLoading(false);
      setErrorMsg("네트워크 오류가 발생했어요. 잠시 후 다시 시도해주세요.");
    }
  };

  const handleSubmit = () => mode === "login" ? handleLogin() : handleSignUp();

  const handleAppleLogin = async () => {
    setErrorMsg("");
    try {
      const { Capacitor } = await import('@capacitor/core');
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'apple',
          options: {
            skipBrowserRedirect: true,
            redirectTo: 'com.crewpick.app://login-callback'
          }
        });
        if (error) throw error;
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: data.url });
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'apple',
          options: { redirectTo: window.location.origin }
        });
        if (error) throw error;
      }
    } catch (e) {
      if (e?.message?.includes('cancel')) return;
      setErrorMsg("Apple 로그인 중 오류가 발생했어요. 다시 시도해주세요.");
    }
  };

  const handleGoogleLogin = async () => {
    setErrorMsg("");
    try {
      const { Capacitor } = await import('@capacitor/core');
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        const { Browser } = await import('@capacitor/browser');

        const platform = Capacitor.getPlatform();
        const redirectScheme = platform === 'ios'
          ? 'com.crewpick.app://login-callback'
          : 'com.crewpick.android://login-callback';

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: redirectScheme,
            skipBrowserRedirect: true,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            },
          },
        });

        if (error) { setErrorMsg(friendlyError(error.message)); return; }
        if (data?.url) {
          await Browser.open({
            url: data.url,
            presentationStyle: 'popover'
          });
        }
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.origin,
          },
        });
        if (error) setErrorMsg(friendlyError(error.message));
      }
    } catch (e) {
      setErrorMsg("로그인 중 오류가 발생했어요. 다시 시도해주세요.");
    }
  };

  return (
    <div style={s.container}>
      {onBack && (
        <div style={s.header}>
          <button style={s.backBtn} onClick={onBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            뒤로
          </button>
        </div>
      )}

      <div style={{ ...s.body, paddingTop: onBack ? 16 : 48 }}>

        {/* 로고 이미지만 크게 - 텍스트 없음 */}
        <div style={s.logoWrap}>
          <img src={logoImg} alt="크루픽 로고" style={{ width: 260, objectFit: "contain" }} />
        </div>

        {/* 탭 (로그인 / 회원가입) */}
        <div style={s.tabRow}>
          <button style={{ ...s.tab, borderBottom: mode === "login" ? "2px solid #E24B4A" : "2px solid transparent", color: mode === "login" ? "#E24B4A" : "#9ca3af" }}
            onClick={() => reset("login")}>로그인</button>
          <button style={{ ...s.tab, borderBottom: mode === "signup" ? "2px solid #E24B4A" : "2px solid transparent", color: mode === "signup" ? "#E24B4A" : "#9ca3af" }}
            onClick={() => reset("signup")}>회원가입</button>
        </div>

        {successMsg && (
          <div style={s.successBox}>
            <span style={{ fontSize: 14 }}>✅</span>
            <span style={{ fontSize: 13, color: "#16a34a", marginLeft: 8, lineHeight: 1.4 }}>{successMsg}</span>
          </div>
        )}

        {errorMsg && (
          <div style={s.errorBox}>
            <span style={{ fontSize: 14 }}>⚠️</span>
            <span style={{ fontSize: 13, color: "#dc2626", marginLeft: 8, lineHeight: 1.4 }}>{errorMsg}</span>
          </div>
        )}

        <div style={s.fieldWrap}>
          <label style={s.label}>이메일</label>
          <input
            style={s.input}
            type="email"
            placeholder="example@email.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrorMsg(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            autoComplete="email"
          />
        </div>

        <div style={s.fieldWrap}>
          <label style={s.label}>비밀번호</label>
          <input
            style={s.input}
            type="password"
            placeholder="비밀번호를 입력하세요"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setErrorMsg(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
          />
        </div>

        {mode === "signup" && (
          <div style={s.fieldWrap}>
            <label style={s.label}>비밀번호 확인</label>
            <input
              style={s.input}
              type="password"
              placeholder="비밀번호를 다시 입력하세요"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setErrorMsg(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoComplete="new-password"
            />
          </div>
        )}

        <button
          style={{ ...s.submitBtn, opacity: loading ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading
            ? (mode === "login" ? "로그인 중..." : "가입 중...")
            : (mode === "login" ? "로그인" : "회원가입")}
        </button>

        {/* 또는 구분선 */}
        <div style={s.dividerRow}>
          <div style={s.dividerLine} />
          <span style={s.dividerText}>또는</span>
          <div style={s.dividerLine} />
        </div>

        {/* Apple 로그인 */}
        <button style={s.appleBtn} onClick={handleAppleLogin}>
          <svg width="18" height="18" viewBox="0 0 814 1000" style={{ marginRight: 8, flexShrink: 0 }} fill="#fff">
            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-32.8-155.5-127.4C46.7 790.7 0 663 0 541.8c0-207.3 135.3-317 269-317 71 0 130.5 46.8 175 46.8 42.3 0 108.5-49.9 190.4-49.9 30.8 0 130.4 2.7 203.5 95.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
          </svg>
          Apple로 로그인
        </button>

        {/* Google 로그인 */}
        <button style={s.googleBtn} onClick={handleGoogleLogin}>
          <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: 8, flexShrink: 0 }}>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.01 24.01 0 000 21.56l7.98-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Google로 로그인
        </button>

        <p style={s.notice}>
          {mode === "login" ? "아직 계정이 없으신가요? " : "이미 계정이 있으신가요? "}
          <button style={s.switchBtn} onClick={() => reset(mode === "login" ? "signup" : "login")}>
            {mode === "login" ? "회원가입" : "로그인"}
          </button>
        </p>
      </div>
    </div>
  );
}

function friendlyError(msg = "") {
  if (msg.includes("Invalid login credentials")) return "이메일 또는 비밀번호가 올바르지 않아요.";
  if (msg.includes("Email not confirmed"))        return "이메일 인증이 필요해요. 메일함을 확인해주세요.";
  if (msg.includes("User already registered"))    return "이미 가입된 이메일이에요. 로그인해주세요.";
  if (msg.includes("Password should be"))         return "비밀번호는 6자 이상이어야 해요.";
  return msg || "오류가 발생했어요. 잠시 후 다시 시도해주세요.";
}

const s = {
  container:  { minHeight: "100dvh", background: "#f5f6fa", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch", maxWidth: 480, margin: "0 auto", width: "100%" },
  header:     { padding: "16px 20px", minHeight: 52 },
  backBtn:    { display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", fontSize: 14, fontWeight: 600, color: "#E24B4A", cursor: "pointer", padding: 0 },
  body:       { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "0 28px 40px" },
  logoWrap:   { display: "flex", justifyContent: "center", marginBottom: 28 },
  tabRow:     { display: "flex", width: "100%", marginBottom: 20, borderBottom: "1px solid #e5e7eb" },
  tab:        { flex: 1, padding: "10px 0", background: "none", border: "none", fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "color 0.15s" },
  successBox: { width: "100%", display: "flex", alignItems: "flex-start", padding: "12px 14px", background: "#f0fdf4", borderRadius: 10, border: "1px solid #bbf7d0", marginBottom: 14 },
  errorBox:   { width: "100%", display: "flex", alignItems: "flex-start", padding: "12px 14px", background: "#fff5f5", borderRadius: 10, border: "1px solid #fecaca", marginBottom: 14 },
  fieldWrap:  { width: "100%", marginBottom: 14 },
  label:      { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 },
  input:      { width: "100%", padding: "13px 14px", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10, color: "#111827", fontSize: 15, outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
  submitBtn:  { width: "100%", padding: "14px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer", marginTop: 6, marginBottom: 14 },
  dividerRow: { display: "flex", alignItems: "center", width: "100%", margin: "4px 0 14px" },
  dividerLine:{ flex: 1, height: 1, background: "#e5e7eb" },
  dividerText:{ padding: "0 12px", fontSize: 12, color: "#9ca3af", flexShrink: 0 },
  appleBtn:   { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "13px 14px", background: "#000", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600, color: "#fff", cursor: "pointer", marginBottom: 14, fontFamily: "inherit" },
  googleBtn:  { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "13px 14px", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 12, fontSize: 15, fontWeight: 600, color: "#374151", cursor: "pointer", marginBottom: 14, fontFamily: "inherit" },
  notice:     { fontSize: 13, color: "#9ca3af", textAlign: "center" },
  switchBtn:  { background: "none", border: "none", fontSize: 13, color: "#E24B4A", fontWeight: 700, cursor: "pointer", padding: 0 },
};